import React from 'react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {fetchBrands} from 'api';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {AsyncSelect, Select} from 'components/ui-kit/Select';
import {ProductGroups} from 'core/constants/productGroups';
import {ROUTE} from 'core/routes';
import {breakpoint} from 'theme';
import {
    GridFieldConfig,
    UserPermission,
    VirtualWarehouseGridAccessors,
    VirtualWarehouseGridFormattedRow,
    VirtualWarehouseGridRawRow,
} from 'types';
import {CUSTOMER_PACKAGE_TYPES} from 'types/gtins';
import {useMatchMedia} from 'utils/custom-hooks';
import {handleNil} from 'utils/handleNil';
import {formatNumber} from 'utils/number';
import {mapEnumToI18nOptions, mapValueIfKeyExist} from 'utils/select';

import {toHex} from 'utils/string';

import {Navigation} from '../Navigation';

import {VirtualWarehouseGridList} from './List';
import {BalanceCell, HeaderControls} from './styled';
import {VirtualWarehouseGridTable} from './Table';

export function convertData(
    data: VirtualWarehouseGridRawRow[] | undefined,
    t: TFunction,
): VirtualWarehouseGridFormattedRow[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [VirtualWarehouseGridAccessors.participant]: handleNil(elem.participant),
        [VirtualWarehouseGridAccessors.name]: handleNil(elem.productName),
        [VirtualWarehouseGridAccessors.gtin]: handleNil(elem.gtin),
        [VirtualWarehouseGridAccessors.code]: handleNil(elem.code, (value: string) => (
            <PermissionAccess
                permissions={[UserPermission.PRODUCT_DETAIL_VIEW, UserPermission.PRODUCT_CARD_VIEW]}
                placeholder={value}
            >
                <Link
                    to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                        id: toHex(`${elem.gtin}§${value}`),
                    })}
                    style={{wordBreak: 'break-all'}}
                >
                    {value}
                </Link>
            </PermissionAccess>
        )),
        [VirtualWarehouseGridAccessors.type]: t(
            mapValueIfKeyExist('dictionaries.products.packageType.', elem.type),
        ),
        [VirtualWarehouseGridAccessors.income]: handleNil(elem.incomeCount, (value: number) =>
            formatNumber(value),
        ),
        [VirtualWarehouseGridAccessors.expense]: handleNil(elem.expenseCount, (value: number) =>
            formatNumber(value),
        ),
        [VirtualWarehouseGridAccessors.trademark]: handleNil(elem.productTrademark),
        [VirtualWarehouseGridAccessors.groupCode]: handleNil(elem.productGroupCode, (value) => (
            <ProductGroupIcon value={value} label />
        )),
        [VirtualWarehouseGridAccessors.balance]: handleNil(elem.remainCount, (value: number) => (
            <BalanceCell isNegative={value < 0}>{formatNumber(value)}</BalanceCell>
        )),
        [VirtualWarehouseGridAccessors.storageName]: handleNil(elem.storageName),
        [VirtualWarehouseGridAccessors.storageAddress]: handleNil(elem.storageAddress),
    }));
}

export const VirtualWarehouseGridUngroupedPage = () => {
    const {t} = useTranslation();
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);

    const getGridColumns = (t: TFunction, brands: any[], groups: any[]): GridFieldConfig[] => [
        // ...(hasParticipantViewPermission
        //     ? [
        //           {
        //               id: VirtualWarehouseGridAccessors.participant,
        //               header: t('pages.virtualWarehouse.owner'),
        //               // filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
        //           },
        //       ]
        //     : []),
        {
            id: VirtualWarehouseGridAccessors.groupCode,
            header: t('pages.virtualWarehouse.groupCode'),
            filter: (
                <Select
                    options={mapEnumToI18nOptions(ProductGroups, 'dictionaries.productGroups')}
                    isClearable
                />
            ),
            width: 150,
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.name,
            header: t('pages.virtualWarehouse.name'),
            filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.gtin,
            header: t('pages.virtualWarehouse.gtin'),
            filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.trademark,
            header: t('pages.virtualWarehouse.trademark'),
            // filter: <Select options={brands} />,
            filter: (
                <AsyncSelect
                    name={'trademark-selector-instances'}
                    defaultOptions
                    loadOptions={async () => {
                        const value = await fetchBrands();
                        return value
                            .sort((a: string, b: string) => a.localeCompare(b))
                            .map((item: string) => ({
                                value: item,
                                label: item,
                            }));
                    }}
                    isClearable
                />
            ),
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.code,
            header: t('pages.virtualWarehouse.markingCodes'),
            // filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.type,
            header: t('pages.virtualWarehouse.packaging'),
            filter: (
                <Select
                    isClearable
                    options={CUSTOMER_PACKAGE_TYPES.map((type) => ({
                        value: type,
                        label: t(`dictionaries.products.packageType.${type}`),
                    }))}
                />
            ),
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.storageName,
            header: t('pages.virtualWarehouse.storageName'),
            filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.storageAddress,
            header: t('pages.virtualWarehouse.storageAddress'),
            filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.participant,
            header: t('pages.virtualWarehouse.owner'),
            filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
            isSortable: false,
        },
    ];

    const pageHeaderControls = undefined;
    // isMobile ? (
    //     <Icon
    //         source={FilterIcon}
    //         width={18}
    //         height={18}
    //         onClick={() => showConfigBlockChanged(!showConfigBlock)}
    //         fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
    //     />
    // ) : (
    //     undefined
    // );

    const headerControls = (
        <HeaderControls>
            <Navigation />
        </HeaderControls>
    );

    return (
        <Page title={t('menu.virtualWarehouse')} controls={pageHeaderControls}>
            {!isMobile && (
                <VirtualWarehouseGridTable
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                    // exportConfig={exportConfig}
                    headerControls={headerControls}
                />
            )}
            {isMobile && (
                <VirtualWarehouseGridList
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                    // exportConfig={exportConfig}
                    headerControls={headerControls}
                />
            )}
        </Page>
    );
};
